import React from "react";
import _ from "lodash-fp";

import { ApolloProvider } from "@apollo/client";
import withApollo from "../lib/apolloClient2";
import { ThemeProvider } from "styled-components";
import SiteLayout from "../src/SiteLayout";
import PrivateRoutes from "../src/PrivateRoutes";
import getTokens from "@kiwicom/orbit-components/lib/getTokens";

import { isServer } from "../src/helpers/isServer";
import getAuthorization from "../src/helpers/getAuthorization";
import { hasScopeFromToken } from "../src/helpers/hasScope";
import { safeRedirect } from "../src/helpers/safeRedirect";
import { hasExportRoute } from "../src/helpers/hasExportRoute";

import "../src/helpers/validators/index";
import { useRouter } from "next/router";
import { useRunOnChange } from "../src/utils/useRunOnChange";

const TOKENS = {
  ...getTokens({
    base: {
      fontFamily:
        "Arial, -apple-system, '.SFNSText-Regular', 'San Francisco', 'Segoe UI', 'Helvetica Neue', 'Lucida Grande', sans-serif;"
    },
    palette: {
      product: {
        normal: "#db0a35",
        normalHover: "#b5002a",
        normalActive: "#8f0026"
      },
      ink: {
        lighter: "#94A8BE"
      }
    }
  }),
  borderColorInput: "#909fb0",
  borderColorCard: "#bac7d5",
  fontSizeHeadingTitle3: "18px"
};

export const AppContext = React.createContext({});

const appRef = React.createRef();

const triggerResize = () => {
  if (isServer) return;
  const height = appRef.current?.clientHeight ?? 800;
  parent.postMessage(`resizeIframe::${height}`, "*");
};

const ToolsApp = ({
  Component,
  pageProps,
  apollo,
  isPrivate,
  useSiteLayout
}) => {
  triggerResize();

  const router = useRouter();

  useRunOnChange(router.asPath, () => {
    if (isServer) return;
    parent.postMessage(
      {
        type: "ROUTER_PUSH",
        url: router.asPath
      },
      "*"
    );
  });

  return (
    <ThemeProvider
      theme={{
        orbit: TOKENS
      }}
    >
      <AppContext.Provider
        value={{
          isPrivate
        }}
      >
        <ApolloProvider client={apollo}>
          <PrivateRoutes router={router} isExport={!useSiteLayout}>
            <SiteLayout enabled={useSiteLayout} ref={appRef}>
              <Component {...pageProps} />
            </SiteLayout>
          </PrivateRoutes>
        </ApolloProvider>
      </AppContext.Provider>
    </ThemeProvider>
  );
};

ToolsApp.getInitialProps = async ({ Component, ctx, router }) => {
  const token = router.query.token || getAuthorization(ctx).token;

  const req = ctx.req;

  const requiredScope = Component.auth?.scope;

  if (requiredScope && !hasScopeFromToken(token, requiredScope)) {
    await safeRedirect("/no-scope", ctx);
  }

  const isExport = isServer && _.isEmpty(req.headers);

  const useSiteLayout = !isExport && !hasExportRoute(router.route);

  const pageProps = Component.getInitialProps
    ? await Component.getInitialProps(ctx, router)
    : {};

  return {
    pageProps,
    useSiteLayout,
    initialToken: token
  };
};

export default withApollo(ToolsApp);
